import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '../../../core/services/account.service';
import { PasswordResetRequest } from '../../../models/password-reset-request';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../shared/material.module';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit {
  resetForm!: FormGroup;
  token!: string;
  hidePassword = true;
  hideConfirmPassword = true;
  errorMessage = '';
  successMessage = '';
  tokenValid = false;

  // Variables para el modal
  showModal = false;
  modalAction: 'submit' | 'cancel' | null = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token') ?? '';

    this.accountService.getByPassToken(this.token).subscribe({
      next: (res) => {
        if (res.exists) {
          this.tokenValid = true;
          this.initForm();
        } else {
          this.tokenValid = false;
          this.errorMessage = 'El enlace para reestablecer la contraseña ha expirado o se ha cancelado';
        }
      },
      error: () => {
        this.tokenValid = false;
        this.errorMessage = 'El enlace para reestablecer la contraseña ha expirado';
      }
    });
  }

  private initForm() {
    this.resetForm = this.fb.group(
      {
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            this.passwordValidation
          ]
        ],
        confirmPassword: ['', Validators.required]
      },
      { validators: this.passwordsMatchValidator }
    );
  }

  get password(): AbstractControl | null {
    return this.resetForm.get('password');
  }

  get confirmPassword(): AbstractControl | null {
    return this.resetForm.get('confirmPassword');
  }

  private passwordValidation(control: AbstractControl): ValidationErrors | null {
    const value = control.value as string;
    if (!value) return null;

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSymbol = /[^A-Za-z0-9]/.test(value);

    return hasUpperCase && hasLowerCase && hasNumber && hasSymbol
      ? null
      : { passwordComplexity: true };
  }

  private passwordsMatchValidator(group: FormGroup): ValidationErrors | null {
    const pass = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    return pass === confirm ? null : { passwordsNotMatching: true };
  }

  togglePasswordVisibility(field: 'password' | 'confirm') {
    if (field === 'password') {
      this.hidePassword = !this.hidePassword;
    } else {
      this.hideConfirmPassword = !this.hideConfirmPassword;
    }
  }

  // Mostrar modal
  confirmAction(action: 'submit' | 'cancel') {
    this.modalAction = action;
    this.showModal = true;
  }

  // Confirmar acción dentro del modal
  proceedAction() {
    this.showModal = false;

    if (this.modalAction === 'submit') {
      this.performSubmit();
    } else if (this.modalAction === 'cancel') {
      this.performCancel();
    }

    this.modalAction = null;
  }

  // Cerrar modal sin acción
  closeModal() {
    this.showModal = false;
    this.modalAction = null;
  }

  private performSubmit(): void {
    if (this.resetForm.invalid) return;

    const request: PasswordResetRequest = {
      passToken: this.token,
      password: this.resetForm.get('password')?.value
    };

    this.accountService.resetPassword(request).subscribe({
      next: () => {
        this.successMessage = 'La contraseña se actualizó correctamente';
        this.errorMessage = '';
        this.resetForm.reset();
      },
      error: (err) => {
        this.errorMessage = err.error?.error || 'Ocurrió un error';
        this.successMessage = '';
      }
    });
  }

  private performCancel(): void {
    this.accountService.cleanPassToken(this.token).subscribe({
      next: () => {
        this.tokenValid = false;
        this.errorMessage = 'El enlace para reestablecer la contraseña ha expirado';
        this.successMessage = '';
      },
      error: () => {
        this.tokenValid = false;
        this.errorMessage = 'El enlace para reestablecer la contraseña ha expirado';
        this.successMessage = '';
      }
    });
  }
}